import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LayoutGrid, MoonStar, CalendarDays, CalendarRange, Music, Mic, PartyPopper, Laugh, Ticket,
  ChevronLeft, ChevronRight, MapPin, Clock, Sparkles, Store, Plus,
} from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SearchBar } from "@/components/SearchBar";
import { EventCard } from "@/components/EventCard";
import { StatusBadge } from "@/components/StatusBadge";
import { FavoriteButton } from "@/components/FavoriteButton";
import { SectionHeader } from "@/components/SectionHeader";
import { FilterSelect } from "@/components/FilterSelect";
import { MapPreview } from "@/components/MapPreview";
import { PublicEventModal } from "@/components/PublicEventModal";
import { useLanguage } from "@/i18n/LanguageContext";

const WDAYS_SHORT_FR = ["lun.", "mar.", "mer.", "jeu.", "ven.", "sam.", "dim."];
const WDAYS_SHORT_EN = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const toIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const TODAY_ISO = toIso(new Date());
const parseDay = (iso) => new Date(`${iso}T12:00:00`);
const locale = (lang) => (lang === "en" ? "en-CA" : "fr-CA");
const fullDate = (iso, lang) =>
  new Intl.DateTimeFormat(locale(lang), { weekday: "long", day: "numeric", month: "long", year: "numeric" }).format(parseDay(iso));
const monthYear = (d, lang) => new Intl.DateTimeFormat(locale(lang), { month: "long", year: "numeric" }).format(d);
const shortMonth = (d, lang) => new Intl.DateTimeFormat(locale(lang), { month: "short" }).format(d).replace(".", "");
const diffDays = (iso) => Math.round((parseDay(iso) - parseDay(TODAY_ISO)) / 86400000);
const isWeekend = (iso) => {
  const wd = parseDay(iso).getDay();
  const diff = diffDays(iso);
  return diff >= 0 && diff <= 7 && (wd === 0 || wd === 6);
};
const matchDateMode = (e, mode) => {
  if (mode === "toutes") return true;
  const diff = diffDays(e.date);
  if (mode === "ce-soir") return diff === 0;
  if (mode === "cette-semaine") return diff >= 0 && diff <= 6;
  if (mode === "ce-week-end") return isWeekend(e.date);
  return true;
};

const QUICK_FILTERS = [
  { key: "tous", label: "Tous", icon: LayoutGrid },
  { key: "ce-soir", label: "Ce soir", icon: MoonStar },
  { key: "cette-semaine", label: "Cette semaine", icon: CalendarDays },
  { key: "ce-week-end", label: "Ce week-end", icon: CalendarRange },
  { key: "concerts", label: "Concerts", icon: Music },
  { key: "karaoke", label: "Karaoké", icon: Mic },
  { key: "soirees", label: "Soirées", icon: PartyPopper },
  { key: "humour", label: "Humour", icon: Laugh },
  { key: "activites", label: "Activités", icon: Ticket },
];

const reveal = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-40px" },
  transition: { duration: 0.5, ease: "easeOut" },
};

const PriceChip = ({ price }) => {
  const { t } = useLanguage();
  return price > 0 ? (
    <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-600">{price} $</span>
  ) : (
    <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-600">{t("Gratuit")}</span>
  );
};

const FeaturedCard = ({ event }) => {
  const navigate = useNavigate();
  const { lang } = useLanguage();
  return (
    <article
      data-testid={`featured-card-${event.slug}`}
      onClick={() => navigate(`/evenement/${event.slug}`)}
      className="group relative cursor-pointer overflow-hidden rounded-3xl border border-slate-100 shadow-[0_4px_20px_rgba(15,23,42,0.08)] transition duration-300 hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(37,99,235,0.16)]"
    >
      <img src={event.image} alt={event.title} loading="lazy" className="h-56 w-full object-cover transition duration-500 group-hover:scale-105 sm:h-64" />
      <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/20 to-transparent" />
      <div className="absolute left-3 top-3">
        <StatusBadge badge={event.badge} />
      </div>
      <FavoriteButton item={event} itemType="evenement" className="absolute right-3 top-3" />
      <div className="absolute inset-x-0 bottom-0 p-4 text-white">
        <h3 className="text-lg font-bold leading-snug">{event.title}</h3>
        <p className="mt-1 text-sm text-white/80">
          {event.venue} • {event.neighborhood}
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs font-medium text-white/90">
          <span className="flex items-center gap-1">
            <CalendarDays className="h-3.5 w-3.5" />
            {fullDate(event.date, lang)}
          </span>
          <span className="flex items-center gap-1">
            <Clock className="h-3.5 w-3.5" />
            {event.time_label}
          </span>
          <PriceChip price={event.price} />
        </div>
      </div>
    </article>
  );
};

const UpcomingRow = ({ event }) => {
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  const d = parseDay(event.date);
  return (
    <article
      data-testid={`upcoming-row-${event.slug}`}
      onClick={() => navigate(`/evenement/${event.slug}`)}
      className="group flex cursor-pointer items-center gap-3 rounded-2xl border border-slate-100 bg-white p-3 shadow-[0_4px_16px_rgba(15,23,42,0.05)] transition duration-300 hover:-translate-y-0.5 hover:border-blue-200 hover:shadow-[0_10px_24px_rgba(37,99,235,0.12)] sm:gap-4"
    >
      <div className="flex w-12 shrink-0 flex-col items-center rounded-2xl bg-[#F8FAFC] py-2">
        <span className="text-lg font-extrabold leading-tight text-slate-900">{d.getDate()}</span>
        <span className="text-[10px] font-semibold uppercase text-slate-400">{shortMonth(d, lang)}</span>
      </div>
      <img src={event.image} alt={event.title} loading="lazy" className="h-16 w-16 shrink-0 rounded-xl object-cover" />
      <div className="min-w-0 flex-1 space-y-0.5">
        <h3 className="truncate text-sm font-semibold text-slate-900">{event.title}</h3>
        <p className="truncate text-xs text-slate-500">
          {event.venue} • {event.neighborhood}
        </p>
        <div className="flex flex-wrap items-center gap-2 pt-0.5">
          <span className="flex items-center gap-1 text-xs text-slate-400">
            <Clock className="h-3 w-3" />
            {event.time_label}
          </span>
          <StatusBadge badge={event.badge} soft />
          <PriceChip price={event.price} />
          {event.price > 0 && (
            <span data-testid="reserve-chip" className="rounded-full bg-[#2563EB] px-2.5 py-1 text-[11px] font-semibold text-white">
              {t("Réserver")}
            </span>
          )}
        </div>
      </div>
      <FavoriteButton item={event} itemType="evenement" inline />
    </article>
  );
};

const MiniCalendar = ({ selected, onSelect, eventDates }) => {
  const now = new Date();
  const [cursor, setCursor] = useState(new Date(now.getFullYear(), now.getMonth(), 1));
  const { t, lang } = useLanguage();
  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const firstWeekday = (new Date(year, month, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [...Array(firstWeekday).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  const wdays = lang === "en" ? WDAYS_SHORT_EN : WDAYS_SHORT_FR;

  return (
    <div data-testid="mini-calendar">
      <div className="flex items-center justify-between">
        <button
          type="button"
          data-testid="cal-prev"
          onClick={() => setCursor(new Date(year, month - 1, 1))}
          className="flex h-8 w-8 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="text-sm font-bold capitalize text-slate-900">
          {monthYear(cursor, lang)}
        </span>
        <button
          type="button"
          data-testid="cal-next"
          onClick={() => setCursor(new Date(year, month + 1, 1))}
          className="flex h-8 w-8 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
      <div className="mt-3 grid grid-cols-7 gap-1 text-center">
        {wdays.map((d) => (
          <span key={d} className="pb-1 text-[10px] font-semibold uppercase text-slate-400">
            {d}
          </span>
        ))}
        {cells.map((day, i) => {
          if (!day) return <span key={`e-${i}`} />;
          const iso = toIso(new Date(year, month, day));
          const hasEvent = eventDates.has(iso);
          const isSelected = selected === iso;
          const isToday = iso === TODAY_ISO;
          return (
            <button
              key={iso}
              type="button"
              data-testid={`cal-day-${iso}`}
              onClick={() => onSelect(isSelected ? null : iso)}
              className={`relative mx-auto flex h-9 w-9 flex-col items-center justify-center rounded-full text-sm transition ${
                isSelected
                  ? "bg-[#2563EB] font-bold text-white"
                  : isToday
                    ? "font-bold text-[#2563EB] ring-1 ring-[#2563EB]"
                    : hasEvent
                      ? "font-semibold text-slate-900 hover:bg-blue-50"
                      : "text-slate-500 hover:bg-slate-100"
              }`}
            >
              {day}
              {hasEvent && !isSelected && <span className="absolute bottom-0.5 h-1 w-1 rounded-full bg-[#2563EB]" />}
            </button>
          );
        })}
      </div>
      {selected && (
        <button
          type="button"
          data-testid="cal-clear"
          onClick={() => onSelect(null)}
          className="mt-3 w-full rounded-full bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-600 transition hover:bg-slate-200"
        >
          {t("Effacer la date")} ({fullDate(selected, lang)})
        </button>
      )}
    </div>
  );
};

export default function EvenementsPage() {
  const [events, setEvents] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [quick, setQuick] = useState("tous");
  const [dateFilter, setDateFilter] = useState("toutes");
  const [catFilter, setCatFilter] = useState("toutes");
  const [quartierFilter, setQuartierFilter] = useState("tous");
  const [prixFilter, setPrixFilter] = useState("tous");
  const [freeOnly, setFreeOnly] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [publishOpen, setPublishOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t, lang } = useLanguage();

  useEffect(() => {
    Promise.all([api.get("/events", { params: { scope: "all" } }), api.get("/cities")])
      .then(([e, v]) => {
        setEvents(e.data);
        setCities(v.data);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  // Retour de connexion avec intention de publication → ouvre la modale automatiquement
  useEffect(() => {
    if (!user) return;
    try {
      if (sessionStorage.getItem("gospot_publish_intent")) {
        sessionStorage.removeItem("gospot_publish_intent");
        setPublishOpen(true);
      }
    } catch {}
  }, [user]);

  const neighborhoods = useMemo(() => [...new Set(events.map((e) => e.neighborhood).filter(Boolean))].sort(), [events]);
  const eventDates = useMemo(() => new Set(events.map((e) => e.date)), [events]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return events
      .filter((e) => {
        if (needle && ![e.title, e.venue, e.neighborhood].filter(Boolean).join(" ").toLowerCase().includes(needle)) return false;
        const quickIsCat = ["concerts", "karaoke", "soirees", "humour", "activites"].includes(quick);
        if (quickIsCat && e.category_key !== quick) return false;
        if (!quickIsCat && !matchDateMode(e, quick)) return false;
        if (!matchDateMode(e, dateFilter)) return false;
        if (catFilter !== "toutes" && e.category_key !== catFilter) return false;
        if (quartierFilter !== "tous" && e.neighborhood !== quartierFilter) return false;
        if ((prixFilter === "gratuit" || freeOnly) && e.price > 0) return false;
        if (prixFilter === "payant" && !(e.price > 0)) return false;
        if (selectedDate && e.date !== selectedDate) return false;
        return true;
      })
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [events, q, quick, dateFilter, catFilter, quartierFilter, prixFilter, freeOnly, selectedDate]);

  const featured = filtered.filter((e) => e.featured);
  const weekend = filtered.filter((e) => e.scope === "weekend" || isWeekend(e.date));
  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach((e) => {
      (map[e.date] = map[e.date] || []).push(e);
    });
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  const resetAll = () => {
    setQ("");
    setQuick("tous");
    setDateFilter("toutes");
    setCatFilter("toutes");
    setQuartierFilter("tous");
    setPrixFilter("tous");
    setFreeOnly(false);
    setSelectedDate(null);
  };

  const publish = () => {
    if (!user) {
      navigate("/connexion");
      return;
    }
    navigate("/publier");
  };

  // Bouton « Publier un événement » (membre sans établissement) : connexion requise, puis modale
  const openPublishModal = () => {
    if (!user) {
      try {
        sessionStorage.setItem("gospot_publish_intent", "1");
      } catch {}
      navigate("/connexion", { state: { from: "/evenements" } });
      return;
    }
    setPublishOpen(true);
  };

  return (
    <main data-testid="evenements-page" className="mx-auto max-w-[1800px] px-4 pb-16 pt-8 sm:px-6 sm:pt-10 lg:px-10">
      <Seo
        title={t("Événements à Québec — GoSpot")}
        siteName="GoSpot"
        description={t("Concerts, karaokés, soirées, activités et événements près de vous à Québec.")}
      />

      <div className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <motion.h1
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: "easeOut" }}
            className="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl"
          >
            {t("Événements à Québec")}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.1 }}
            className="mt-2 text-base text-slate-600"
          >
            {t("Concerts, karaokés, soirées, activités et événements près de vous.")}
          </motion.p>
        </div>
        <motion.button
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: 0.15 }}
          type="button"
          data-testid="publish-public-event-button"
          onClick={openPublishModal}
          className="flex shrink-0 items-center gap-2 self-start rounded-full bg-[#2563EB] px-6 py-3 text-sm font-semibold text-white shadow-[0_6px_18px_rgba(37,99,235,0.3)] transition hover:bg-[#1D4ED8] active:scale-95 sm:self-auto"
        >
          <Plus className="h-4 w-4" />
          {t("Publier un événement")}
        </motion.button>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, delay: 0.18 }}
        className="mt-6 max-w-2xl"
      >
        <SearchBar
          cities={cities}
          initialQuery={q}
          onSearch={(val) => setQ(val)}
          placeholder={t("Rechercher un événement, un artiste ou un lieu…")}
        />
      </motion.div>

      {/* FILTRES RAPIDES */}
      <div className="no-scrollbar mt-6 flex snap-x gap-2.5 overflow-x-auto pb-1">
        {QUICK_FILTERS.map((f) => {
          const Icon = f.icon;
          const active = quick === f.key;
          return (
            <button
              key={f.key}
              type="button"
              data-testid={`quick-filter-${f.key}`}
              onClick={() => setQuick(f.key)}
              className={`flex shrink-0 snap-start items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition duration-300 ${
                active
                  ? "border-[#2563EB] bg-[#2563EB] text-white shadow-[0_6px_18px_rgba(37,99,235,0.25)]"
                  : "border-slate-200 bg-white text-slate-700 hover:-translate-y-0.5 hover:border-blue-200"
              }`}
            >
              <Icon className={`h-4 w-4 ${active ? "text-white" : "text-slate-500"}`} />
              {t(f.label)}
            </button>
          );
        })}
      </div>

      <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_330px]">
        {/* COLONNE PRINCIPALE */}
        <div className="min-w-0">
          {featured.length > 0 && (
            <motion.section {...reveal} data-testid="featured-section">
              <SectionHeader icon={Sparkles} iconColor="#F59E0B" title="À la une" testId="voir-tout-une" />
              <div className="grid gap-5 sm:grid-cols-2">
                {featured.map((e) => (
                  <FeaturedCard key={e.slug} event={e} />
                ))}
              </div>
            </motion.section>
          )}

          {weekend.length > 0 && (
            <motion.section {...reveal} className="mt-10" data-testid="weekend-section">
              <SectionHeader icon={CalendarRange} title="Ce week-end" linkTo="/ce-soir" testId="voir-tout-weekend" />
              <div className="no-scrollbar flex snap-x gap-4 overflow-x-auto pb-1 sm:grid sm:grid-cols-2 sm:overflow-visible">
                {weekend.map((e) => (
                  <EventCard key={e.slug} event={e} className="min-w-[300px] snap-start sm:min-w-0" />
                ))}
              </div>
            </motion.section>
          )}

          <motion.section {...reveal} className="mt-10" data-testid="upcoming-section">
            <SectionHeader
              icon={CalendarDays}
              title={selectedDate ? (lang === "en" ? `Events on ${fullDate(selectedDate, lang)}` : `Événements le ${fullDate(selectedDate, lang)}`) : "À venir"}
              testId="voir-tout-avenir"
            />
            {loading ? (
              [0, 1, 2].map((i) => <div key={i} className="mb-3 h-24 animate-pulse rounded-2xl bg-white" />)
            ) : grouped.length === 0 ? (
              <div data-testid="evenements-no-results" className="rounded-3xl border border-slate-100 bg-white p-10 text-center">
                <p className="font-semibold text-slate-900">{t("Aucun événement trouvé")}</p>
                <p className="mt-1 text-sm text-slate-500">{t("Essaie d’ajuster ta recherche ou tes filtres.")}</p>
                <button
                  type="button"
                  data-testid="evenements-reset-button"
                  onClick={resetAll}
                  className="mt-4 rounded-full bg-[#2563EB] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#1D4ED8]"
                >
                  {t("Réinitialiser les filtres")}
                </button>
              </div>
            ) : (
              <div className="space-y-6">
                {grouped.map(([date, list]) => (
                  <div key={date}>
                    <h3 className="mb-3 text-xs font-bold uppercase tracking-wider text-slate-400">{fullDate(date, lang)}</h3>
                    <div className="space-y-3">
                      {list.map((e) => (
                        <UpcomingRow key={e.slug} event={e} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.section>
        </div>

        {/* COLONNE DROITE */}
        <aside className="space-y-5 self-start lg:sticky lg:top-24">
          <div className="rounded-3xl border border-slate-100 bg-white p-5 shadow-sm">
            <h2 className="mb-3 text-base font-bold text-slate-900">{t("Calendrier")}</h2>
            <MiniCalendar selected={selectedDate} onSelect={setSelectedDate} eventDates={eventDates} />
          </div>

          <div className="rounded-3xl border border-slate-100 bg-white p-5 shadow-sm" data-testid="detailed-filters">
            <h2 className="text-base font-bold text-slate-900">{t("Filtres détaillés")}</h2>
            <div className="mt-4 flex flex-wrap gap-2.5">
              <FilterSelect
                label="Date"
                value={dateFilter}
                onChange={setDateFilter}
                testId="evfilter-date"
                options={[
                  { value: "toutes", label: "Toutes les dates" },
                  { value: "ce-soir", label: "Ce soir" },
                  { value: "cette-semaine", label: "Cette semaine" },
                  { value: "ce-week-end", label: "Ce week-end" },
                ]}
              />
              <FilterSelect
                label="Catégorie"
                value={catFilter}
                onChange={setCatFilter}
                testId="evfilter-categorie"
                options={[
                  { value: "toutes", label: "Toutes catégories" },
                  { value: "concerts", label: "Concerts" },
                  { value: "karaoke", label: "Karaoké" },
                  { value: "soirees", label: "Soirées" },
                  { value: "humour", label: "Humour" },
                  { value: "activites", label: "Activités" },
                ]}
              />
              <FilterSelect
                label="Quartier"
                value={quartierFilter}
                onChange={setQuartierFilter}
                testId="evfilter-quartier"
                options={[
                  { value: "tous", label: "Tous les quartiers" },
                  ...neighborhoods.map((n) => ({ value: n, label: n })),
                ]}
              />
              <FilterSelect
                label="Prix"
                value={prixFilter}
                onChange={setPrixFilter}
                testId="evfilter-prix"
                options={[
                  { value: "tous", label: "Tous les prix" },
                  { value: "gratuit", label: "Gratuit" },
                  { value: "payant", label: "Payant" },
                ]}
              />
            </div>
            <label className="mt-4 flex cursor-pointer items-center gap-2.5 text-sm text-slate-700">
              <input
                type="checkbox"
                data-testid="free-only-checkbox"
                checked={freeOnly}
                onChange={(e) => setFreeOnly(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 accent-[#2563EB]"
              />
              {t("Gratuit seulement")}
            </label>
            <button
              type="button"
              data-testid="reset-detailed-filters"
              onClick={resetAll}
              className="mt-4 w-full rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-600 transition hover:border-blue-300 hover:text-[#2563EB]"
            >
              {t("Réinitialiser")}
            </button>
          </div>

          <div className="overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-sm" data-testid="events-map-card">
            <h2 className="px-5 pb-3 pt-4 text-base font-bold text-slate-900">{t("Autour de vous")}</h2>
            <div className="px-3 pb-3">
              <MapPreview markers={filtered} showButton={false} />
            </div>
          </div>
        </aside>
      </div>

      {/* BANDEAU PROPRIÉTAIRE */}
      <motion.section {...reveal} className="mt-14" data-testid="owner-banner">
        <div className="flex flex-col items-start gap-5 rounded-3xl border border-blue-100 bg-[#EDF3FE] p-8 sm:flex-row sm:items-center sm:justify-between sm:p-10">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#2563EB]">
              <Store className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900 sm:text-xl">{t("Propriétaire d’un établissement ?")}</h2>
              <p className="mt-1 max-w-lg text-sm leading-relaxed text-slate-600">
                {t("Rejoignez GoSpot et faites découvrir vos événements à des milliers de personnes.")}
              </p>
            </div>
          </div>
          <button
            type="button"
            data-testid="publish-event-button"
            onClick={publish}
            className="shrink-0 rounded-full bg-[#2563EB] px-7 py-3.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#1D4ED8] active:scale-95"
          >
            {t("Publier un événement")}
          </button>
        </div>
      </motion.section>

      <PublicEventModal open={publishOpen} onClose={() => setPublishOpen(false)} />
    </main>
  );
}
